/**
 * ユーザーセッション管理ユーティリティ
 *
 * ローカルストレージを使用して匿名ユーザーIDを永続化する。
 * クロスページのCVRトラッキングに使用（カードクリック後のページ遷移追跡）。
 *
 * 注意: このモジュールはメインスレッドでのみ使用可能。
 * WebWorker（StoresDispatcher）では localStorage にアクセスできないため、
 * メインスレッドで取得したIDをWorkerに渡す設計になっている。
 */

const USER_ID_KEY = 'talknUserId';
const USER_ID_EXPIRY_KEY = 'talknUserIdExpiry';
const EXPIRY_DAYS = 30;

/**
 * 匿名ユーザーIDを取得または生成する
 *
 * - ローカルストレージに有効なIDがあればそれを返す
 * - なければ新しいIDを生成してストレージに保存する
 * - SSR環境や localStorage が使えない場合はセッション限りのIDを生成する
 *
 * @returns 匿名ユーザーID（例: "usr_1234567890_abc123def456"）
 */
export function getTalknUserId(): string {
  if (typeof localStorage === 'undefined') {
    return generateUserId();
  }

  try {
    const stored = localStorage.getItem(USER_ID_KEY);
    const expiry = localStorage.getItem(USER_ID_EXPIRY_KEY);

    if (stored && expiry && Date.now() < parseInt(expiry, 10)) {
      return stored;
    }

    const newId = generateUserId();
    const expiryTime = Date.now() + EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(USER_ID_KEY, newId);
    localStorage.setItem(USER_ID_EXPIRY_KEY, expiryTime.toString());
    return newId;
  } catch {
    // localStorage が利用不可（プライベートブラウジング等）の場合
    return generateUserId();
  }
}

/**
 * 匿名ユーザーIDを生成する
 */
function generateUserId(): string {
  const randomPart =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID().replace(/-/g, '').slice(0, 12)
      : Math.random().toString(36).slice(2, 14);
  return `usr_${Date.now()}_${randomPart}`;
}
